// Station locations, read from the CSVs in ./stations.
// With CRA (react-scripts), importing a .csv returns the URL of the bundled file,
// so the contents are fetched at runtime. Types for "*.csv" live in csv.d.ts.
import { useEffect, useState } from "react";
import rawsCsvUrl from "./stations/raws_loc.csv";
import fawnCsvUrl from "./stations/fawn_loc.csv";
import asosCsvUrl from "./stations/asos_loc.csv";

export type Network = "RAWS" | "FAWN" | "ASOS";

export interface StationLocation {
    network: Network;
    id: string;             // RAWS: 4-letter id (fsum); FAWN: numeric id; ASOS: ICAO-style code
    name: string;
    lat: number;
    lon: number;
    elevation: number | null; // metres
    nwsId?: string;         // RAWS only: NWS/FEMS station number (e.g. "82002"), when present
    agency?: string;        // RAWS only
}

const toNum = (v: string | undefined): number | null => {
    if (v == null || v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

// Minimal CSV parser: none of these files contain quoted fields or commas inside values.
const parseCsv = (text: string): Record<string, string>[] => {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim() !== "");
    const header = lines[0].split(",").map(h => h.trim());
    return lines.slice(1).map(line => {
        const cells = line.split(",");
        return Object.fromEntries(header.map((h, i) => [h, (cells[i] ?? "").trim()]));
    });
};

const dedupe = (rows: StationLocation[]) => {
    const seen = new Set<string>();
    return rows.filter(r => {
        if (seen.has(r.id)) return false;
        seen.add(r.id);
        return true;
    });
};

const hasCoords = (r: StationLocation) => Number.isFinite(r.lat) && Number.isFinite(r.lon);

// raws_loc.csv: id,loc,lat,lon,ele,nid,nwsid,agn,utc
const parseRaws = (text: string): StationLocation[] =>
    dedupe(
        parseCsv(text).map(r => {
            const nws = toNum(r.nwsid);
            return {
                network: "RAWS" as const,
                id: r.id,
                name: r.loc,
                lat: Number(r.lat),
                lon: Number(r.lon),
                elevation: toNum(r.ele),
                nwsId: nws != null ? String(Math.round(nws)) : undefined,
                agency: r.agn || undefined,
            };
        })
    ).filter(hasCoords);

// fawn_loc.csv: name,id,lat,lon,ele
const parseFawn = (text: string): StationLocation[] =>
    dedupe(
        parseCsv(text).map(r => ({
            network: "FAWN" as const,
            id: r.id,
            name: r.name,
            lat: Number(r.lat),
            lon: Number(r.lon),
            elevation: toNum(r.ele),
        }))
    ).filter(hasCoords);

// asos_loc.csv: station,lon,lat,elevation
const parseAsos = (text: string): StationLocation[] =>
    dedupe(
        parseCsv(text).map(r => ({
            network: "ASOS" as const,
            id: r.station,
            name: r.station,
            lat: Number(r.lat),
            lon: Number(r.lon),
            elevation: toNum(r.elevation),
        }))
    ).filter(hasCoords);

export interface StationLocations {
    raws: StationLocation[];
    fawn: StationLocation[];
    asos: StationLocation[];
}

const fetchText = async (url: string) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Could not load ${url} (HTTP ${res.status})`);
    return res.text();
};

// Fetched once per page load and shared by every caller.
let locationsPromise: Promise<StationLocations> | null = null;

export const loadStationLocations = (): Promise<StationLocations> => {
    if (!locationsPromise) {
        locationsPromise = Promise.all([fetchText(rawsCsvUrl), fetchText(fawnCsvUrl), fetchText(asosCsvUrl)])
            .then(([raws, fawn, asos]) => ({ raws: parseRaws(raws), fawn: parseFawn(fawn), asos: parseAsos(asos) }))
            .catch(err => {
                locationsPromise = null; // allow a retry on next mount
                throw err;
            });
    }
    return locationsPromise;
};

export const useStationLocations = () => {
    const [data, setData] = useState<StationLocations | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        loadStationLocations()
            .then(d => { if (!cancelled) setData(d); })
            .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); });
        return () => { cancelled = true; };
    }, []);

    return { data, error };
};